import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CloudUpload  as UploadIcon,
  DeleteOutline as DeleteIcon,
  AttachFile    as FileIcon,
  Image         as ImageIcon,
  PictureAsPdf  as PdfIcon,
  Description   as DocIcon,
  TableChart    as SheetIcon,
  Slideshow     as SlideIcon,
  CheckCircle   as CheckIcon,
  Business      as OrgIcon,
  AccountTree   as LobIcon,
  WarningAmber  as WarningIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { PageHeader } from '../components/shared/PageHeader'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { lobsApi, Lob } from '../lib/api/tenantsApi'
import { useOrgSelector } from '../context/OrgSelectorContext'
import {
  orgEmailFilesApi,
  OrgEmailFile,
  formatBytes,
  ACCEPTED_FILE_TYPES,
} from '../lib/api/orgEmailFilesApi'

// ── file-type icon ────────────────────────────────────────────────────────────
function FileTypeIcon({ type, size = 22 }: { type: string; size?: number }) {
  const sx = { fontSize: size }
  if (type.startsWith('image/'))                               return <ImageIcon sx={{ ...sx, color: '#10b981' }} />
  if (type === 'application/pdf')                              return <PdfIcon   sx={{ ...sx, color: '#ef4444' }} />
  if (type.includes('word') || type.includes('document'))      return <DocIcon   sx={{ ...sx, color: '#3b82f6' }} />
  if (type.includes('sheet') || type.includes('excel'))        return <SheetIcon sx={{ ...sx, color: '#16a34a' }} />
  if (type.includes('presentation') || type.includes('power')) return <SlideIcon sx={{ ...sx, color: '#f97316' }} />
  return <FileIcon sx={{ ...sx, color: themeColors.text.secondary }} />
}

function fileTypeLabel(type: string): string {
  if (type.startsWith('image/'))                               return 'Image'
  if (type === 'application/pdf')                              return 'PDF'
  if (type.includes('word') || type.includes('document'))      return 'Word Doc'
  if (type.includes('sheet') || type.includes('excel'))        return 'Spreadsheet'
  if (type.includes('presentation') || type.includes('power')) return 'Presentation'
  return 'File'
}

// ── drop zone ─────────────────────────────────────────────────────────────────
interface DropZoneProps {
  onFiles:  (files: File[]) => void
  uploading: boolean
  disabled?: boolean
  disabledMessage?: string
}

function DropZone({ onFiles, uploading, disabled, disabledMessage }: DropZoneProps) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    if (disabled) return
    const files = Array.from(e.dataTransfer.files)
    if (files.length) onFiles(files)
  }, [onFiles, disabled])

  return (
    <Box
      onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !uploading && !disabled && inputRef.current?.click()}
      sx={{
        border: `2px dashed ${dragging ? themeColors.primary : themeColors.border}`,
        borderRadius: '14px',
        p: 5,
        textAlign: 'center',
        cursor: uploading || disabled ? 'not-allowed' : 'pointer',
        bgcolor: disabled ? '#f5f5f5' : dragging ? '#f0f4ff' : '#fafafa',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s',
        '&:hover': (uploading || disabled) ? {} : {
          borderColor: themeColors.primary,
          bgcolor: '#f0f4ff',
        },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={ACCEPTED_FILE_TYPES}
        onChange={e => {
          if (e.target.files) onFiles(Array.from(e.target.files))
          e.target.value = ''
        }}
      />

      <Box sx={{
        width: 56, height: 56, borderRadius: '50%',
        bgcolor: disabled ? '#e5e7eb' : '#ede9fe',
        display: 'flex', alignItems: 'center',
        justifyContent: 'center', mx: 'auto', mb: 2,
      }}>
        <UploadIcon sx={{ fontSize: 28, color: disabled ? themeColors.text.muted : themeColors.primary }} />
      </Box>

      <Typography sx={{ fontSize: 15, fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>
        {disabled ? (disabledMessage ?? 'Select a Line of Business to upload') : dragging ? 'Drop files here' : 'Drag & drop files here'}
      </Typography>
      {!disabled && (
        <Typography sx={{ fontSize: 13, color: themeColors.text.secondary, mb: 2 }}>
          or click on browse — PDFs, images, Word, Excel, PowerPoint
        </Typography>
      )}
      {!disabled && (
        <Button
          variant="outlined"
          size="small"
          disabled={uploading}
          sx={{
            fontSize: 12, fontWeight: 600, textTransform: 'none',
            borderRadius: '8px', borderColor: themeColors.primary,
            color: themeColors.primary, px: 2,
            '&:hover': { bgcolor: '#f0f4ff', borderColor: themeColors.primaryDark },
          }}
        >
          Browse Files
        </Button>
      )}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress sx={{ borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: themeColors.primary } }} />
          <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, mt: 0.75 }}>Uploading…</Typography>
        </Box>
      )}
    </Box>
  )
}

// ── file card ─────────────────────────────────────────────────────────────────
function FileCard({
  file,
  showLobBadge,
  onDelete,
  deleting,
}: {
  file:         OrgEmailFile
  showLobBadge: boolean
  onDelete:     () => void
  deleting:     boolean
}) {
  const date = new Date(file.createdAt).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  })

  const handleCardClick = () => {
    if (deleting || !file.url) return
    window.open(file.url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.75,
        borderRadius: '10px', border: `1px solid ${themeColors.border}`,
        bgcolor: '#fff', transition: 'all 0.15s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: themeColors.shadow.sm,
          borderColor: themeColors.primary,
          bgcolor: '#fafafa',
        },
        opacity: deleting ? 0.5 : 1,
      }}
    >
      <Box sx={{
        width: 42, height: 42, borderRadius: '10px',
        bgcolor: '#f8f8ff', display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
        border: `1px solid ${themeColors.border}`,
      }}>
        <FileTypeIcon type={file.contentType} size={22} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13, fontWeight: 600, color: themeColors.text.primary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {file.fileName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
          <Chip
            label={fileTypeLabel(file.contentType)}
            size="small"
            sx={{ height: 18, fontSize: 10.5, bgcolor: '#ede9fe', color: themeColors.primary, fontWeight: 600, border: 'none' }}
          />
          {showLobBadge && (
            <Chip
              icon={<LobIcon sx={{ fontSize: '11px !important' }} />}
              label={file.lobName ?? 'No LOB'}
              size="small"
              sx={{ height: 18, fontSize: 10.5, bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 600, border: 'none' }}
            />
          )}
          <Typography sx={{ fontSize: 11.5, color: themeColors.text.secondary }}>
            {formatBytes(file.sizeBytes)} · {date}
          </Typography>
        </Box>
      </Box>

      <Tooltip title="Delete file">
        <span>
          <IconButton
            size="small"
            disabled={deleting}
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.error, bgcolor: themeColors.errorLight } }}
          >
            {deleting
              ? <CircularProgress size={16} />
              : <DeleteIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

// ── main page ─────────────────────────────────────────────────────────────────
const EmailFilesPage = observer(function EmailFilesPage() {
  const qc   = useQueryClient()
  const user = authStore.user

  // Org context: SuperAdmin → top-nav selector. OrgAdmin/Normal → fixed on their own user.
  const { selectedOrgId: masterOrgId, selectedOrgName: masterOrgName, isSuperAdmin } = useOrgSelector()

  // OrgAdmin: has a fixed org but no fixed LOB (sees/admins every LOB in their org).
  // Normal User: has both org and LOB fixed — fully scoped already, nothing to pick.
  const isOrgAdmin   = !isSuperAdmin && !!user?.orgId && !user?.lobId
  const isNormalUser = !isSuperAdmin && !!user?.orgId && !!user?.lobId

  // The org this screen operates on. SuperAdmin: whatever is selected in the top navigation.
  // OrgAdmin / Normal User: always their own org — never user-editable here.
  const effectiveOrgId   = isSuperAdmin ? masterOrgId : (user?.orgId ?? null)
  const effectiveOrgName = isSuperAdmin ? masterOrgName : (user?.orgName ?? user?.orgId ?? null)

  // ── LOB selection (SuperAdmin / OrgAdmin only — Normal User's LOB is fixed) ───
  const canPickLob = isSuperAdmin || isOrgAdmin
  const [selectedLobId, setSelectedLobId] = useState<string>('')

  // Reset the LOB choice whenever the underlying org changes (its LOB list changed too)
  useEffect(() => {
    setSelectedLobId('')
  }, [effectiveOrgId])

  const { data: lobs = [], isLoading: lobsLoading } = useQuery<Lob[]>({
    queryKey: ['lobs-for-email-files', effectiveOrgId],
    queryFn:  () => lobsApi.getAll(effectiveOrgId!),
    enabled:  canPickLob && !!effectiveOrgId,
  })

  const effectiveLobId = isNormalUser ? (user?.lobId ?? null) : (selectedLobId || null)
  const effectiveLobName = isNormalUser
    ? (user?.lobName ?? user?.lobId ?? null)
    : (lobs.find(l => l.id === selectedLobId)?.name ?? null)

  // A valid Organisation + Line of Business must both be resolved before a file can be
  // uploaded — mirrors the backend's mandatory LOB validation.
  const hasValidContext = !!effectiveOrgId && !!effectiveLobId

  // Viewing the list only needs an Organisation. With no LOB chosen (SuperAdmin/OrgAdmin),
  // every LOB's files in that org are shown, each tagged with its LOB name.
  const canViewFiles  = !!effectiveOrgId
  const showLobBadge  = canPickLob && !selectedLobId

  // Track which file IDs are being deleted
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const [deletingFile, setDeletingFile] = useState<OrgEmailFile | null>(null)

  // ── Fetch files ───────────────────────────────────────────────────────────
  // Always scoped to the resolved Organisation + LOB context (isolated per LOB).
  const filesQueryKey = ['org-email-files', effectiveOrgId ?? 'none', effectiveLobId ?? 'none']

  const { data: files = [], isLoading: filesLoading } = useQuery<OrgEmailFile[]>({
    queryKey: filesQueryKey,
    queryFn:  () => orgEmailFilesApi.getAll(effectiveOrgId, effectiveLobId),
    enabled:  canViewFiles,
  })

  // ── Upload mutation ───────────────────────────────────────────────────────
  const [uploading, setUploading] = useState(false)

  const handleFiles = async (incoming: File[]) => {
    if (!hasValidContext) {
      toast.warning('Select a Line of Business before uploading files')
      return
    }

    const accepted = incoming.filter(f => {
      const ok =
        f.type.startsWith('image/')     ||
        f.type === 'application/pdf'    ||
        f.type.includes('word')         ||
        f.type.includes('document')     ||
        f.type.includes('sheet')        ||
        f.type.includes('excel')        ||
        f.type.includes('presentation') ||
        f.type.includes('power')
      if (!ok) toast.warning(`"${f.name}" is not a supported file type`)
      return ok
    })
    if (!accepted.length) return

    const oversized = accepted.filter(f => f.size > 25 * 1024 * 1024)
    if (oversized.length) {
      oversized.forEach(f => toast.error(`"${f.name}" exceeds the 25 MB limit`))
      return
    }

    setUploading(true)
    try {
      for (const file of accepted) {
        await orgEmailFilesApi.upload(file, effectiveOrgId, effectiveLobId)
      }
      await qc.invalidateQueries({ queryKey: filesQueryKey })
      toast.success(`${accepted.length} file${accepted.length > 1 ? 's' : ''} uploaded successfully`)
    } catch {
      toast.error('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  // ── Delete mutation ───────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      setDeletingIds(prev => new Set(prev).add(id))
      await orgEmailFilesApi.delete(id)
      return id
    },
    onSuccess: async (id) => {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s })
      await qc.invalidateQueries({ queryKey: filesQueryKey })
      toast.success('File deleted')
    },
    onError: (_err, id) => {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s })
      toast.error('Failed to delete file')
    },
  })

  return (
    <Box>
      <PageHeader
        title="Email Files"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb={true}
      />

      <Box sx={{ px: 3, pb: 4, maxWidth: 820 }}>

        {/* ── Info banner ── */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
          p: 2, mb: 3, borderRadius: '12px',
          bgcolor: '#f0f4ff', border: `1px solid #c7d7f5`,
        }}>
          <CheckIcon sx={{ fontSize: 18, color: themeColors.primary, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, color: themeColors.text.primary, lineHeight: 1.6 }}>
            Files uploaded here are stored in <strong>Azure Blob Storage</strong> and linked to the
            organisation and Line of Business. They appear as <strong>pre-uploaded attachments</strong>
            in the email composer — click the <strong>Files</strong> button to pick and attach them instantly.
          </Typography>
        </Box>

        {/* ── SuperAdmin with no org selected in the top nav ── */}
        {isSuperAdmin && !effectiveOrgId && (
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
            p: 2.5, mb: 3, borderRadius: '12px',
            bgcolor: '#fff7ed', border: `1px solid #fed7aa`,
          }}>
            <WarningIcon sx={{ fontSize: 20, color: '#ea580c', mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: '#9a3412', lineHeight: 1.6 }}>
              Select an organisation from the <strong>top navigation bar</strong> to configure
              its Email Files.
            </Typography>
          </Box>
        )}

        {/* ── Org + LOB context card ── */}
        {effectiveOrgId && (
          <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>

              {/* Organisation — always read-only on this screen */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: canPickLob ? 2.5 : 0 }}>
                <OrgIcon sx={{ fontSize: 16, color: themeColors.primary }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: themeColors.primary }}>
                  {effectiveOrgName ?? effectiveOrgId}
                </Typography>
                <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, ml: 0.5 }}>
                  {isSuperAdmin ? '(from top navigation)' : 'your organisation'}
                </Typography>
              </Box>

              {/* Line of Business — selectable for SuperAdmin / OrgAdmin */}
              {canPickLob && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <LobIcon sx={{ fontSize: 20, color: themeColors.primary }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: themeColors.text.primary }}>
                        Line of Business
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, mt: '2px' }}>
                        Select the LOB to configure — required before uploading
                      </Typography>
                    </Box>
                  </Box>

                  <FormControl fullWidth size="small" disabled={lobsLoading || lobs.length === 0}>
                    <InputLabel sx={{ fontSize: 13 }}>Line of Business</InputLabel>
                    <Select
                      value={selectedLobId}
                      label="Line of Business"
                      onChange={e => setSelectedLobId(e.target.value)}
                      sx={{ fontSize: 13, borderRadius: '8px' }}
                    >
                      {lobs.map(lob => (
                        <MenuItem key={lob.id} value={lob.id} sx={{ fontSize: 13 }}>
                          {lob.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {lobsLoading && (
                    <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, mt: 1 }}>
                      Loading Lines of Business…
                    </Typography>
                  )}
                  {!lobsLoading && lobs.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, mt: 1 }}>
                      This organisation has no Lines of Business configured yet.
                    </Typography>
                  )}
                  {!lobsLoading && selectedLobId && effectiveLobName && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CheckIcon sx={{ fontSize: 14, color: themeColors.success }} />
                      <Typography sx={{ fontSize: 12, color: themeColors.success, fontWeight: 600 }}>
                        Configuring files for: {effectiveLobName}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Normal User — LOB is fixed, just show it */}
              {isNormalUser && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <LobIcon sx={{ fontSize: 16, color: themeColors.primary }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: themeColors.primary }}>
                    {effectiveLobName ?? '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, ml: 0.5 }}>
                    your line of business
                  </Typography>
                </Box>
              )}

            </CardContent>
          </Card>
        )}

        {/* ── Upload card ── */}
        <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: themeColors.text.primary, mb: 0.5 }}>
              Upload Files
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: themeColors.text.secondary, mb: 2 }}>
              Supported: PDF, JPEG, PNG, GIF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx) — Max 25 MB per file
            </Typography>
            <DropZone
              onFiles={handleFiles}
              uploading={uploading}
              disabled={!hasValidContext}
              disabledMessage={
                isSuperAdmin && !effectiveOrgId
                  ? 'Select an organisation in the top navigation first'
                  : 'Select a Line of Business above to upload'
              }
            />
          </CardContent>
        </Card>

        {/* ── File list ── */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: themeColors.text.primary }}>
              Uploaded Files
            </Typography>
            {files.length > 0 && (
              <Chip
                label={files.length}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: 11, bgcolor: '#ede9fe', color: themeColors.primary, fontWeight: 700 }}
              />
            )}
          </Box>

          {canViewFiles && canPickLob && (
            <Typography sx={{ fontSize: 12, color: themeColors.text.secondary, mb: 1.5 }}>
              {selectedLobId
                ? `Showing files for: ${effectiveLobName ?? 'selected LOB'}`
                : 'Showing files from all Lines of Business in this organisation'}
            </Typography>
          )}

          {!canViewFiles ? (
            <Box sx={{
              py: 6, textAlign: 'center',
              border: `1px solid ${themeColors.border}`,
              borderRadius: '14px', bgcolor: '#fafafa',
            }}>
              <FileIcon sx={{ fontSize: 40, color: themeColors.text.muted, mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: themeColors.text.secondary }}>
                Select an organisation in the top navigation to view its files
              </Typography>
            </Box>
          ) : filesLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: '10px' }} />
              ))}
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{
              py: 6, textAlign: 'center',
              border: `1px solid ${themeColors.border}`,
              borderRadius: '14px', bgcolor: '#fafafa',
            }}>
              <FileIcon sx={{ fontSize: 40, color: themeColors.text.muted, mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: themeColors.text.secondary }}>
                No files uploaded yet
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: themeColors.text.muted, mt: 0.5 }}>
                Upload files above to make them available in the email composer
              </Typography>
            </Box>
          ) : (
            <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {files.map((file, i) => (
                    <Box key={file.id}>
                      {i > 0 && <Divider sx={{ mb: 1 }} />}
                      <FileCard
                        file={file}
                        showLobBadge={showLobBadge}
                        onDelete={() => setDeletingFile(file)}
                        deleting={deletingIds.has(file.id)}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

      </Box>

      <ConfirmDialog
        open={Boolean(deletingFile)}
        title="Delete File"
        description={`Are you sure you want to delete "${deletingFile?.fileName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setDeletingFile(null)}
        onConfirm={() => {
          if (deletingFile) {
            deleteMut.mutate(deletingFile.id)
            setDeletingFile(null)
          }
        }}
      />
    </Box>
  )
})

export default EmailFilesPage
